import { SentryClass, TSentryCaptureContext, TSentryCommonConfig } from './SentryClass';
import { SENTRY_DSN } from '@env';
import { APP_NAME, APP_VERSION } from 'src/app/settings';

type SentryBrowserModule = typeof import('@sentry/browser');

let _sentryBrowser: SentryBrowserModule | undefined;

const THIRD_PARTY_DOMAINS = [
  'amplitude.com',
  'tag-manager.photosi.com',
  'bat.bing.com',
  'browser-intake-datadoghq.com',
];

// Browser-storage-unavailable signatures (broken/quota-exhausted IndexedDB backing store,
// aborted transactions). Same underlying condition surfaces at every SDK call site that
// happens to be running when it occurs (remove/replaceProducts/setProductsBackgroundColor/
// addProducts/...), both as caught SDK errors and as unhandled promise rejections — grouping
// them by fingerprint below keeps this one issue in Sentry instead of one per call site.
const STORAGE_UNAVAILABLE_PATTERNS = [
  /failed to open database/i,
  /backing store/i,
  /idb write failed/i,
  /transaction aborted/i,
];

function isStorageUnavailableMessage(message: string): boolean {
  return STORAGE_UNAVAILABLE_PATTERNS.some(pattern => pattern.test(message));
}
// Known-benign errors we don't want cluttering Sentry. Each rule is checked against the
// original exception in beforeSend; add new ones here instead of another inline `if`.
const IGNORED_ERROR_RULES: { reason: string; matches: (error: unknown) => boolean }[] = [
  {
    reason: 'fetch AbortController abort',
    matches: error => error instanceof Error && error.name === 'AbortError',
  },
  {
    reason: 'known third-party domain noise',
    matches: error => error instanceof Error && THIRD_PARTY_DOMAINS.some(domain => error.message.includes(domain)),
  },
  {
    reason: 'axios xhr adapter abort on navigation/unmount',
    matches: error =>
      error instanceof Error &&
      (error as { code?: string }).code === 'ECONNABORTED' &&
      error.message === 'Request aborted',
  },
];

class Sentry extends SentryClass {
  public init(App: React.ComponentType<Record<string, unknown>>) {
    if (this.isSentryInitialized) return App;

    if (__DEV__) {
      console.debug('[Vendor] [Sentry] disabled in local environment');
      return App;
    }

    if (!SENTRY_DSN) {
      console.error('[Vendor] [Sentry] DSN is missing');
      return App;
    }

    Promise.all([import('@sentry/browser'), import('@sentry/wasm')])
      .then(([sentryModule, { wasmIntegration }]) => {
        _sentryBrowser = sentryModule;
        sentryModule.init({
          dsn: SENTRY_DSN,
          tracesSampleRate: 1.0,
          release: `${APP_NAME}@${APP_VERSION}`,
          dist: process.env.SENTRY_DIST,
          integrations: [wasmIntegration()],
          ignoreErrors: ['NS_ERROR_ABORT'],
          beforeSend: (event, hint) => {
            const error = hint?.originalException;

            if (IGNORED_ERROR_RULES.some(rule => rule.matches(error))) return null;

            const exceptionMessages =
              event.exception?.values
                ?.map(value => value.value)
                .filter((value): value is string => typeof value === 'string') ?? [];

            if (exceptionMessages.some(isStorageUnavailableMessage)) {
              event.fingerprint = ['indexeddb-storage-unavailable'];
              event.tags = { ...event.tags, 'storage.unavailable': true };
            }

            return event;
          },
          beforeBreadcrumb: (breadcrumb, hint) => {
            if (breadcrumb.category === 'ui.click' && hint?.event?.target) {
              const el = hint.event.target;
              const testId = getDataTestIdFromElement(el);
              if (testId) {
                breadcrumb.data = { ...breadcrumb.data, testId };
                breadcrumb.message = `Clicked: ${testId}`;
              }
            }
            return breadcrumb;
          },
        });
        this.isSentryInitialized = true;
        console.info('[Vendor] [Sentry] initialized');
      })
      .catch(error => {
        console.error('[Vendor] [Sentry] failed to initialize', error);
      });

    return App;
  }

  // Required by abstract base — not used since init() is fully overridden above
  public initSentry(App: React.ComponentType<Record<string, unknown>>, _config: TSentryCommonConfig) {
    return App;
  }

  public setUserInfo(userId: string | null, userType: string) {
    if (this.isSentryInitialized) {
      _sentryBrowser?.setUser(userId ? { id: userId, userType } : null);
    }
  }

  captureException(error: any, context?: TSentryCaptureContext): void {
    if (this.isSentryInitialized) {
      _sentryBrowser?.captureException(error, context);
    }
  }
}

function getDataTestIdFromElement(element?: HTMLElement | null) {
  while (!!element) {
    if (element.getAttribute?.('data-testid')) {
      return element.getAttribute('data-testid');
    }
    element = element.parentElement;
  }
  return null;
}

const SentryObj = new Sentry();
// @ts-ignore
window['Sentry'] = SentryObj;

export default SentryObj;
